import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import React, { useEffect } from "react";
import {
  MAutocomplete,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import { useFormContext } from "react-hook-form";
import {
  useEquipment,
  useReagents,
  useTypesUnits,
  useUser,
} from "@services/servicesGenericsTables";
import useFilteredProcedures from "@app/pages/samples/hook/useFilteredProcedures";
import { InputAdornment } from "@mui/material";
import { OPTIONS_YES_NO } from "./utils/constants";

const PreparationIodo131 = () => {
  const form = useFormContext();
  const { userList } = useUser();
  const { equipmentList } = useEquipment();
  const { reagentsList } = useReagents();
  const { typesUnitsis } = useTypesUnits();
  const filteredProcedures = useFilteredProcedures("PRE");

  const { currentPreparation } = useAnalysis();
  const regentSelected = form.watch("scintillator_lot_number");

  useEffect(() => {
    if (regentSelected) {
      form.setValue(
        "carrier_concentration",
        regentSelected.concentration || "",
        { shouldValidate: true }
      );
    }
  }, [regentSelected]);

  useEffect(() => {
    const planchette = parseFloat(form.watch("planchette")) || 0;
    const planchetteWithPrecipitate =
      parseFloat(form.watch("planchette_with_precipitate")) || 0;

    const mgTorta = (planchetteWithPrecipitate - planchette) * 1000;
    form.setValue("mg_torta", mgTorta.toFixed(2), { shouldValidate: true });
  }, [form.watch("planchette"), form.watch("planchette_with_precipitate")]);

  useEffect(() => {
    const mgTorta = parseFloat(form.watch("mg_torta")) || 0;
    const carrierConcentration =
      parseFloat(form.watch("carrier_concentration")) || 0;

    if (carrierConcentration !== 0) {
      const performance = mgTorta / carrierConcentration;
      form.setValue("preparation_performance", performance.toFixed(3), {
        shouldValidate: true,
      });
    } else {
      form.setValue("preparation_performance", "", { shouldValidate: true });
    }
  }, [form.watch("mg_torta"), form.watch("carrier_concentration")]);

  return (
    <>
      {currentPreparation.index !== undefined && (
        <ResponsiveFormContainer columns={5}>
          <MAutocomplete
            key={JSON.stringify(form.getValues("analysis_analyst"))}
            name="analysis_analyst"
            options={userList}
            label="Seleccione un analista"
            getOptionLabel={(option) => option.username || ""}
            validation={{ required: "El analista es requerido" }}
            disabled={!currentPreparation.canEdit}
          />

          <MAutocomplete
            key={JSON.stringify(form.getValues("preparation_procedure"))}
            name="preparation_procedure"
            options={filteredProcedures}
            label="Procedimiento MLPMA"
            getOptionLabel={(option) => `${option.numop}` || ""}
            //validation={{ required: "El procedimiento es requerido" }}
            disabled={!currentPreparation.canEdit}
          />
          <MDatePicker
            name="start_date"
            pickerType="datetime"
            label="Fecha y hora de inicio"
            validation={{
              required: "La fecha es requerida",
            }}
            disabled={!currentPreparation.canEdit}
          />

          <MDatePicker
            name="end_date"
            pickerType="datetime"
            label="Fecha y hora de fin"
            validation={{
              required: "La fecha es requerida",
            }}
            disabled={!currentPreparation.canEdit}
          />

          <MTextField
            key={JSON.stringify(form.getValues("analysis_amount_sample"))}
            name="analysis_amount_sample"
            placeholder="Cantidad de muestra"
            label="Cantidad de muestra"
            type="number"
            validation={{ required: "La cantidad de muestra es requerida" }}
            fullWidth
            disabled={!currentPreparation.canEdit}
          />

          <MAutocomplete
            key={JSON.stringify(form.getValues("analysis_equipment"))}
            name="analysis_equipment"
            options={equipmentList}
            label="Seleccione un Equipo"
            getOptionLabel={(option) =>
              `${option.codigo} - ${option.descripcion}` || ""
            }
            validation={{ required: "El equipo es requerido" }}
            multiple={true}
            disabled={!currentPreparation.canEdit}
          />

          <MAutocomplete
            key={JSON.stringify(form.getValues("scintillator_lot_number"))}
            name="scintillator_lot_number"
            options={reagentsList.filter(
              (reagent) =>
                reagent.end_date_use === null && reagent.start_date_use != null
            )}
            label="Seleccione Centelleador Nº de lote"
            getOptionLabel={(option) =>
              `${option.name}${
                option.number_lot ? ` - ${option.number_lot}` : ""
              } ${option.reactive_number ? ` - ${option.reactive_number}` : ""}
                    ${
                      !option.number_lot && !option.reactive_number
                        ? ` - Sin lote`
                        : ""
                    }`
            }
            validation={{ required: "El reactivo es requerido" }}
            disabled={!currentPreparation.canEdit}
          />

          <MTextField
            name="carrier_concentration"
            placeholder={"Concentración Portador"}
            label={"Concentración Portador"}
            type="text"
            fullWidth
            disabled
          />

          <MTextField
            name="planchette"
            placeholder={"Plancheta"}
            label={"Plancheta"}
            type="number"
            validation={{ required: "La plancheta es requerida" }}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">{"(g)"}</InputAdornment>
              ),
            }}
            disabled={!currentPreparation.canEdit}
          />
          <MTextField
            name="planchette_with_precipitate"
            placeholder={"Plancheta con precipitado"}
            label={"Plancheta con precipitado"}
            type="number"
            validation={{
              required: "La Plancheta con precipitado es requerida",
            }}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">{"(g)"}</InputAdornment>
              ),
            }}
            disabled={!currentPreparation.canEdit}
          />
          <MTextField
            name="mg_torta"
            placeholder="mg de torta"
            label="mg de torta"
            type="number"
            fullWidth
            disabled
          />
          <MTextField
            name="preparation_performance"
            placeholder="Rendimiento"
            label="Rendimiento"
            type="number"
            fullWidth
            disabled
          />

          <MAutocomplete
            key={JSON.stringify(form.getValues("analysis_unit_types"))}
            name="analysis_unit_types"
            options={typesUnitsis}
            label="Seleccione un tipo de unidad"
            getOptionLabel={(option) =>
              `${option.codigo} | ${option.descripcion}`
            }
            validation={{ required: "El tipo de unidad es requerido" }}
            disabled={!currentPreparation.canEdit}
          />
          <MAutocomplete
            key={JSON.stringify(
              form.getValues("yellow_precipitate_silver_nitrate")
            )}
            name="yellow_precipitate_silver_nitrate"
            options={OPTIONS_YES_NO}
            label="¿Ha habido formación de precipitado amarillo añadir nitrato de plata?"
            getOptionLabel={(option) => option.label}
            validation={{
              required:
                "La respuesta a la formación de precipitado amarillo es requerida",
            }}
            disabled={!currentPreparation.canEdit}
          />
          <MAutocomplete
            key={JSON.stringify(form.getValues("violet_coloration"))}
            name="violet_coloration"
            options={OPTIONS_YES_NO}
            label="¿Ha habido coloración violeta en la fase orgánica?"
            getOptionLabel={(option) => option.label}
            validation={{
              required:
                "La respuesta a la coloración violeta en la fase orgánica es requerida",
            }}
            disabled={!currentPreparation.canEdit}
          />
        </ResponsiveFormContainer>
      )}
    </>
  );
};

export default PreparationIodo131;
